import { useEffect, useRef, useState, useCallback } from "react";
import Quill from "quill";
// import "quill/dist/quill.snow.css";
import "./quill-snow-custom.css";  // Custom Quill CSS without content resets - uses Bootstrap styles
import "./QuillText.css";
import "./QuillText.editor.css";
import {
  ALLOWED_FORMATS,
  IMAGE_STYLE_VALUES,
  TOOLBAR_OPTIONS,
  registerBootstrapFormats,
  BOOTSTRAP_TABLE_STYLES,
  BOOTSTRAP_TABLE_BORDER_STYLES,
  BOOTSTRAP_TABLE_OTHER_STYLES,
  parseTableRowId,
  buildTableRowId,
  fixEditorTableStructure,
  postProcessQuillHtml,
} from "../../../utils/quill/quill-config";
import { renderQuillDeltaToHtml } from "../../../utils/quill/quill-delta-to-html";
import { MediaBrowserModal } from "../../modals/MediaBrowserModal";
import {
  extractStyledColorSegmentsFromDelta,
  normalizePlainParagraphSpacingInDelta,
  parseSerializedValue,
  serializedDeltaContainsTables,
  serializedHtmlContainsTables,
  serializeEditorValue,
  type ColorSegment,
} from "./content";
import {
  changeEditorFormValue,
  dotNotationGet,
  getEditorContext,
} from "./editor-context";
import {
  getAddButtonWrappersInBounds,
} from "./add-buttons";
import { useAddButtonsVisibility } from "./hooks/useAddButtonsVisibility";
import { LinkTooltip } from "./components/LinkTooltip";
import { ImageToolbar } from "./components/ImageToolbar";

type QuillTextEditorProps = {
  delta?: string;
  __easyblocks?: {
    path?: string;
  };
};

export function QuillTextEditor(props: QuillTextEditorProps) {
  const EDITOR_SHORTCUT_KEYS = new Set([
    "x",
    "c",
    "v",
    "a",
    "z",
    "y",
    "b",
    "i",
    "u",
    "k",
  ]);
  const EDITOR_NAVIGATION_KEYS = new Set([
    "ArrowUp",
    "ArrowDown",
    "ArrowLeft",
    "ArrowRight",
    "Home",
    "End",
    "PageUp",
    "PageDown",
  ]);
  const shouldCaptureEditorShortcut = (event: {
    key: string;
    ctrlKey?: boolean;
    metaKey?: boolean;
  }) => {
    if (event.key === "Escape") return false;
    if (event.key === "Backspace" || event.key === "Delete") return true;
    if (!(event.ctrlKey || event.metaKey)) return false;
    return EDITOR_SHORTCUT_KEYS.has(event.key.toLowerCase());
  };
  const stopEditorShortcutPropagation = (event: {
    key: string;
    ctrlKey?: boolean;
    metaKey?: boolean;
    stopPropagation: () => void;
  }) => {
    if (!shouldCaptureEditorShortcut(event) && !EDITOR_NAVIGATION_KEYS.has(event.key)) return;
    event.stopPropagation();
  };

  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const quillRef = useRef<Quill | null>(null);
  const lastSerializedRef = useRef<string>("");
  const [currentValue, setCurrentValue] = useState<string>("");
  const resizeRafRef = useRef<number | null>(null);
  const instanceIdRef = useRef(
    `eb-quill-${Math.random().toString(36).slice(2, 10)}`
  );
  const pickerClickHandlersRef = useRef<Map<HTMLElement, () => void>>(new Map());
  // Track if we're currently editing to prevent external updates from overwriting
  const isEditingRef = useRef<boolean>(false);
  const editingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // HTML view mode state
  const [isHtmlMode, setIsHtmlMode] = useState<boolean>(false);
  const [htmlContent, setHtmlContent] = useState<string>("");
  const htmlTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  // Link tooltip state
  const [linkTooltipVisible, setLinkTooltipVisible] = useState<boolean>(false);
  const [linkUrl, setLinkUrl] = useState<string>("");
  const [linkText, setLinkText] = useState<string>("");
  const [linkNewWindow, setLinkNewWindow] = useState<boolean>(false);
  const linkSelectionRef = useRef<{ index: number; length: number } | null>(null);
  const linkTooltipRef = useRef<HTMLDivElement | null>(null);
  const [isImageModalOpen, setIsImageModalOpen] = useState<boolean>(false);
  const imageSelectionRef = useRef<{ index: number; length: number } | null>(
    null
  );
  const mediaBrowserInstanceIdRef = useRef<string>(
    `media-browser-quill-${Math.random().toString(36).slice(2, 11)}`
  );
  const floatingImageToolbarRef = useRef<HTMLDivElement | null>(null);
  const selectedImageNodeRef = useRef<HTMLImageElement | null>(null);
  const floatingTableToolbarRef = useRef<HTMLDivElement | null>(null);
  const currentTableCellsRef = useRef<HTMLElement[]>([]);
  const [tableToolbarState, setTableToolbarState] = useState<{
    visible: boolean;
    top: number;
    left: number;
    styles: string[];
    hasHeader: boolean;
    cellHAlign: string;
    cellVAlign: string;
  }>({ visible: false, top: 0, left: 0, styles: [], hasHeader: false, cellHAlign: "", cellVAlign: "" });
  const lastKnownImageIndexRef = useRef<number | null>(null);
  const [floatingImageToolbarState, setFloatingImageToolbarState] = useState<{
    visible: boolean;
    top: number;
    left: number;
    style: string;
    alt: string;
    width: string;
    height: string;
    src: string;
    isExternal: boolean;
    index: number | null;
  }>({
    visible: false,
    top: 0,
    left: 0,
    style: "",
    alt: "",
    width: "",
    height: "",
    src: "",
    isExternal: false,
    index: null,
  });
  const buildSerializedEditorValue = useCallback(
    (quill: Quill, html?: string) => {
      const processedHtml = html ?? postProcessQuillHtml(quill.root.innerHTML);
      const rawDelta = quill.getContents();
      const hasTables =
        serializedHtmlContainsTables(processedHtml) || serializedDeltaContainsTables(rawDelta);
      const delta = hasTables
        ? rawDelta
        : normalizePlainParagraphSpacingInDelta(rawDelta);
      const renderedDeltaHtml = renderQuillDeltaToHtml(JSON.stringify(delta));
      return serializeEditorValue(
        delta,
        hasTables ? processedHtml : renderedDeltaHtml
      );
    },
    []
  );

  const persistEditorValue = useCallback(
    (quill: Quill, html?: string) => {
      const serialized = buildSerializedEditorValue(quill, html);
      lastSerializedRef.current = serialized;
      changeEditorFormValue(props.__easyblocks?.path, serialized);
      return serialized;
    },
    [buildSerializedEditorValue, props.__easyblocks?.path]
  );

  const captureColorSegmentsOverlappingRange = useCallback(
    (quill: Quill, range: { index: number; length: number }): ColorSegment[] => {
      if (range.length <= 0) return [];
      const fullDelta = quill.getContents() as any;
      const ops = Array.isArray(fullDelta?.ops) ? fullDelta.ops : [];
      const rangeStart = range.index;
      const rangeEnd = range.index + range.length;
      const segments: ColorSegment[] = [];
      let cursor = 0;

      for (const op of ops) {
        const insert = op?.insert;
        const attributes = (op?.attributes ?? {}) as Record<string, unknown>;
        const opLength = typeof insert === "string" ? insert.length : 1;
        if (opLength <= 0) continue;
        const color = typeof attributes.color === "string" ? attributes.color : "";
        const opStart = cursor;
        const opEnd = cursor + opLength;
        const overlapsSelection = opStart < rangeEnd && opEnd > rangeStart;
        if (overlapsSelection && color && color !== "no-color") {
          segments.push({ index: opStart, length: opLength, color });
        }
        cursor += opLength;
      }

      return segments;
    },
    []
  );

  const restoreColorSegmentsInRange = useCallback(
    (quill: Quill, segments: ColorSegment[]) => {
      for (const segment of segments) {
        if (!segment.color || segment.length <= 0) continue;
        quill.formatText(
          segment.index,
          segment.length,
          "color",
          segment.color,
          "user"
        );
      }
    },
    []
  );

  const applyInlineFormatPreservingColor = useCallback(
    (quill: Quill, formatName: "bold" | "italic" | "underline" | "strike") => {
      const range = quill.getSelection();
      if (!range) return;
      const hadSelection = range.length > 0;
      const colorSegments = hadSelection
        ? captureColorSegmentsOverlappingRange(quill, range)
        : [];
      const currentFormat = quill.getFormat(range) as Record<string, unknown>;
      const isActive = Boolean(currentFormat[formatName]);

      quill.format(formatName, !isActive, "user");

      if (hadSelection && colorSegments.length > 0) {
        restoreColorSegmentsInRange(quill, colorSegments);
      }
    },
    [captureColorSegmentsOverlappingRange, restoreColorSegmentsInRange]
  );

  const isExternalImageUrl = useCallback((rawUrl: string): boolean => {
    if (!rawUrl || typeof window === "undefined") return false;
    try {
      const parsed = new URL(rawUrl, window.location.href);
      const currentHost = window.location.hostname;
      if (!parsed.hostname || !currentHost) return false;
      return parsed.hostname !== currentHost;
    } catch {
      return false;
    }
  }, []);

  const getSelectedImageInfo = useCallback((quill: Quill) => {
    const selection = quill.getSelection();
    const candidateIndexes: number[] = [];
    const pushCandidate = (idx: number) => {
      if (!Number.isFinite(idx)) return;
      if (!candidateIndexes.includes(idx)) {
        candidateIndexes.push(idx);
      }
    };
    const activeElement =
      typeof document !== "undefined" ? document.activeElement : null;
    if (selection) {
      // Only treat an image as selected when Quill selection is exactly one
      // character long (the image embed). This avoids selecting adjacent images
      // while user highlights regular text near them.
      if (selection.length === 1) {
        pushCandidate(selection.index);
        // Runtime evidence shows Quill can expose image leaf at index+1 while
        // selection reports the embed start at index.
        pushCandidate(selection.index + 1);
      }
    } else if (
      (
        floatingImageToolbarRef.current &&
        activeElement instanceof Node &&
        floatingImageToolbarRef.current.contains(activeElement)
      ) &&
      lastKnownImageIndexRef.current !== null
    ) {
      pushCandidate(lastKnownImageIndexRef.current);
      pushCandidate(lastKnownImageIndexRef.current + 1);
      pushCandidate(lastKnownImageIndexRef.current - 1);
    }
    for (const idx of candidateIndexes) {
      if (idx < 0) continue;
      const [leaf] = quill.getLeaf(idx);
      const domNode = leaf?.domNode as HTMLImageElement | undefined;
      if (domNode?.tagName === "IMG") {
        const blot = Quill.find(domNode);
        const blotIndex =
          blot && typeof quill.getIndex === "function"
            ? quill.getIndex(blot as any)
            : idx;
        const formatIndex = blotIndex;
        const format = quill.getFormat(formatIndex, 1) as Record<string, any>;
        const style =
          typeof format.imageStyle === "string"
            ? format.imageStyle
            : (domNode.getAttribute("data-eb-image-style") ?? "");
        const alt =
          typeof format.imageAlt === "string"
            ? format.imageAlt
            : typeof format.alt === "string"
            ? format.alt
            : (domNode.getAttribute("alt") ?? "");
        const width =
          typeof format.imageWidth === "string"
            ? format.imageWidth
            : typeof format.width === "string"
            ? format.width
            : (domNode.getAttribute("width") ?? "");
        const height =
          typeof format.imageHeight === "string"
            ? format.imageHeight
            : typeof format.height === "string"
            ? format.height
            : (domNode.getAttribute("height") ?? "");
        return {
          index: idx,
          formatIndex,
          style,
          alt,
          width,
          height,
          src: domNode.currentSrc || domNode.getAttribute("src") || "",
          imageNode: domNode,
        };
      }
    }

    return {
      index: null as number | null,
      style: "",
      formatIndex: null as number | null,
      alt: "",
      width: "",
      height: "",
      src: "",
      imageNode: null,
    };
  }, []);

  const deleteImageAtIndex = useCallback((index: number | null): boolean => {
    const quill = quillRef.current;
    if (!quill || index === null) return false;
    const candidates = [index, index + 1, index - 1].filter(
      (candidate, position, arr) =>
        candidate >= 0 &&
        candidate < quill.getLength() &&
        arr.indexOf(candidate) === position
    );
    let targetIndex: number | null = null;
    let imageNode: HTMLElement | null = null;
    for (const candidate of candidates) {
      const [leaf] = quill.getLeaf(candidate);
      const node = leaf?.domNode as HTMLElement | undefined;
      if (node?.tagName === "IMG") {
        targetIndex = candidate;
        imageNode = node;
        break;
      }
    }
    if (targetIndex === null || !imageNode) return false;
    const resolvedSrc =
      (imageNode as HTMLImageElement).currentSrc ||
      imageNode.getAttribute("src") ||
      "";
    const beforeImgCount = quill.root.querySelectorAll("img").length;
    const imageBlot = Quill.find(imageNode);
    const blotIndex =
      imageBlot && typeof quill.getIndex === "function"
        ? quill.getIndex(imageBlot as any)
        : null;
    if (imageBlot && typeof (imageBlot as any).remove === "function") {
      (imageBlot as any).remove();
      quill.update("user" as any);
      const nextCursor = Math.max(blotIndex ?? targetIndex, 0);
      quill.setSelection(nextCursor, 0, "silent");
    } else {
      quill.deleteText(targetIndex, 1, "user");
    }
    const afterImgCount = quill.root.querySelectorAll("img").length;
    const sameSrcCount = Array.from(quill.root.querySelectorAll("img")).filter(
      (img) =>
        ((img as HTMLImageElement).currentSrc || img.getAttribute("src") || "") ===
        resolvedSrc
    ).length;
    return true;
  }, []);

  const syncImageStyleControlState = useCallback(() => {
    const quill = quillRef.current;
    if (!quill) return;

    const { index, formatIndex, style, alt, width, height, src, imageNode } =
      getSelectedImageInfo(quill);
    if (index === null || !imageNode) {
      if (selectedImageNodeRef.current) {
        selectedImageNodeRef.current.classList.remove(
          "eb-QuillText__image-selected"
        );
        selectedImageNodeRef.current = null;
      }
      setFloatingImageToolbarState((prev) =>
        prev.visible ||
        prev.style ||
        prev.alt ||
        prev.width ||
        prev.height ||
        prev.src ||
        prev.index !== null
          ? {
              ...prev,
              visible: false,
              style: "",
              alt: "",
              width: "",
              height: "",
              src: "",
              isExternal: false,
              index: null,
            }
          : prev
      );
      return;
    }

    if (selectedImageNodeRef.current) {
      selectedImageNodeRef.current.classList.remove(
        "eb-QuillText__image-selected"
      );
      selectedImageNodeRef.current = null;
    }

    imageNode.classList.add("eb-QuillText__image-selected");
    selectedImageNodeRef.current = imageNode;

    lastKnownImageIndexRef.current = formatIndex ?? index;

    const wrapperEl = wrapperRef.current;
    const containerEl = containerRef.current;
    if (wrapperEl && containerEl && imageNode) {
      const wrapperRect = wrapperEl.getBoundingClientRect();
      const containerRect = containerEl.getBoundingClientRect();
      const imageRect = imageNode.getBoundingClientRect();
      const toolbarWidth = floatingImageToolbarRef.current?.offsetWidth ?? 320;
      const toolbarHeight = floatingImageToolbarRef.current?.offsetHeight ?? 120;
      const margin = 8;

      // Calculate the Quill toolbar height (distance from wrapper top to container top)
      const quillToolbarHeight = containerRect.top - wrapperRect.top;
      const minTop = quillToolbarHeight + margin;

      // Check if image is visible within the wrapper bounds
      const imageTopInWrapper = imageRect.top - wrapperRect.top;
      const imageBottomInWrapper = imageRect.bottom - wrapperRect.top;

      // If image is completely outside the wrapper, hide the toolbar but keep state
      if (imageBottomInWrapper < 0 || imageTopInWrapper > wrapperRect.height) {
        setFloatingImageToolbarState((prev) =>
          prev.visible
            ? {
                ...prev,
                visible: false,
              }
            : prev
        );
        return;
      }

      // Calculate preferred position above the image top
      let top = imageRect.top - wrapperRect.top - 44;

      // Don't let it go above the Quill toolbar
      if (top < minTop) {
        top = Math.max(imageRect.top - wrapperRect.top + margin, minTop);
      }

      // If that would be below the wrapper, clamp to bottom
      const maxTop = wrapperRect.height - toolbarHeight - margin;
      if (top > maxTop) {
        top = maxTop;
      }

      const centeredLeft =
        imageRect.left - wrapperRect.left + imageRect.width / 2 - toolbarWidth / 2;
      const maxLeft = Math.max(wrapperRect.width - toolbarWidth - margin, margin);
      const left = Math.min(Math.max(centeredLeft, margin), maxLeft);

      setFloatingImageToolbarState({
        visible: true,
        top,
        left,
        style: IMAGE_STYLE_VALUES.includes(style as any) ? style : "centered",
        alt,
        width,
        height,
        src,
        isExternal: isExternalImageUrl(src),
        index: formatIndex ?? index,
      });

      // Close table toolbar when image toolbar opens
      setTableToolbarState((prev) => (prev.visible ? { ...prev, visible: false } : prev));
    }
  }, [getSelectedImageInfo, isExternalImageUrl]);

  const applyImageStyleAtSelection = useCallback(
    (nextStyle: string) => {
      const quill = quillRef.current;
      if (!quill) return;

      const selectionBefore = quill.getSelection();
      const selectedInfo = getSelectedImageInfo(quill);
      const index =
        selectedInfo.formatIndex ??
        selectedInfo.index ??
        (selectionBefore && selectionBefore.length > 0
          ? selectionBefore.index
          : null) ??
        lastKnownImageIndexRef.current;
      if (index === null) {
        syncImageStyleControlState();
        return;
      }

      const value = nextStyle ? nextStyle : false;
      quill.setSelection(index, 1, "silent");
      quill.format("imageStyle", value, "user");
      quill.focus({ preventScroll: true });
      if (selectionBefore) {
        quill.setSelection(selectionBefore.index, selectionBefore.length, "silent");
      } else {
        quill.setSelection(index, 1, "silent");
      }
      requestAnimationFrame(syncImageStyleControlState);
    },
    [getSelectedImageInfo, syncImageStyleControlState]
  );

  const applyImageMetadataAtSelection = useCallback(
    (next: { alt?: string; width?: string; height?: string }) => {
      const quill = quillRef.current;
      if (!quill) return;
      const selectionBefore = quill.getSelection();
      const selectedInfo = getSelectedImageInfo(quill);
      const index =
        selectedInfo.formatIndex ??
        selectedInfo.index ??
        (selectionBefore && selectionBefore.length > 0
          ? selectionBefore.index
          : null) ??
        lastKnownImageIndexRef.current;
      if (index === null) {
        syncImageStyleControlState();
        return;
      }

      if (Object.prototype.hasOwnProperty.call(next, "alt")) {
        const value = String(next.alt ?? "");
        quill.setSelection(index, 1, "silent");
        quill.format("imageAlt", value, "user");
      }
      if (Object.prototype.hasOwnProperty.call(next, "width")) {
        const value = String(next.width ?? "").trim();
        quill.setSelection(index, 1, "silent");
        quill.format("imageWidth", value ? value : false, "user");
      }
      if (Object.prototype.hasOwnProperty.call(next, "height")) {
        const value = String(next.height ?? "").trim();
        quill.setSelection(index, 1, "silent");
        quill.format("imageHeight", value ? value : false, "user");
      }
      quill.focus({ preventScroll: true });
      quill.setSelection(index, 1, "silent");
      requestAnimationFrame(syncImageStyleControlState);
    },
    [getSelectedImageInfo, syncImageStyleControlState]
  );

  // Subscribe to form changes
  useEffect(() => {
    const path = props.__easyblocks?.path;
    if (!path || typeof window === "undefined") return;
    const editorContext = getEditorContext();
    if (!editorContext?.form) return;

    const valuePath = `${path}.delta`;
    const unsubscribe = editorContext.form.subscribe(
      ({ values }: { values: unknown }) => {
        // Skip external updates while user is actively editing
        if (isEditingRef.current) {
          return;
        }
        const next = dotNotationGet(values, valuePath);
        setCurrentValue(typeof next === "string" ? next : "");
      },
      { values: true }
    );

    return unsubscribe;
  }, [props.__easyblocks?.path]);

  // Initialize Quill editor
  useEffect(() => {
    if (!containerRef.current || quillRef.current) return;

    // Register custom formats once
    registerBootstrapFormats();

    const Delta = Quill.import("delta");
    containerRef.current.innerHTML = "";
    const parentEl = containerRef.current.parentElement;
    if (parentEl) {
      const prevSibling = containerRef.current.previousElementSibling;
      if (prevSibling?.classList.contains("ql-toolbar")) {
        prevSibling.remove();
      }
      const ownedToolbar = parentEl.querySelector(
        `.ql-toolbar[data-eb-quill-owner="${instanceIdRef.current}"]`
      );
      ownedToolbar?.remove();
    }
    
    // Create Quill instance - let Quill create the toolbar automatically
    const quill = new Quill(containerRef.current, {
        theme: "snow",
        placeholder: "Write text…",
        modules: {
          toolbar: TOOLBAR_OPTIONS,
          table: true,
          clipboard: {
            matchers: [
              // Block images, videos, and iframes from being pasted - must be first
              ["IMG", () => new Delta()],
              ["VIDEO", () => new Delta()],
              ["IFRAME", () => new Delta()],
              ["HR", () => new Delta().insert({ hr: true })],
              // Strip ALL inline styles and unwanted classes from all elements
              [Node.ELEMENT_NODE, (node: any, delta: any) => {
                // Remove unwanted attributes
                if (node.removeAttribute) {
                  node.removeAttribute("style");
                  // Remove deprecated align attribute (use Quill's align format instead)
                  node.removeAttribute("align");
                  // Remove other common unwanted attributes from Word/PDF
                  node.removeAttribute("lang");
                  node.removeAttribute("xml:lang");
                  node.removeAttribute("dir");
                }

                // Remove unwanted classes but keep Bootstrap classes that Quill recognizes
                // Quill's ClassAttributors will convert fs-*, fw-*, text-* to delta attributes
                if (node.classList && node.classList.length > 0) {
                  const classesToRemove: string[] = [];
                  node.classList.forEach((className: string) => {
                    // Keep only Bootstrap classes that Quill's ClassAttributors recognize
                    const isAllowedClass =
                      /^fs-\d+$/.test(className) ||           // Font size: fs-14, fs-16, etc.
                      /^fw-(thin|extralight|light|normal|medium|semibold|bold|extrabold|black)$/.test(className) || // Font weight
                      /^text-(white|black|primary|secondary|success|info|warning|danger|primary-\d+|secondary-\d+|no-color)$/.test(className) || // Text color
                      /^text-(start|center|end|justify)$/.test(className) || // Text alignment (Quill uses align attribute, but keep these)
                      /^align-(top|middle|bottom)$/.test(className) || // Vertical alignment for table cells
                      /^table/.test(className) ||             // Table classes
                      /^img-fluid$/.test(className) ||        // Image classes
                      /^image-(center|left|right)$/.test(className) ||
                      /^d-block$/.test(className) ||
                      /^mx-auto$/.test(className) ||
                      /^float-(start|end)$/.test(className);

                    if (!isAllowedClass) {
                      classesToRemove.push(className);
                    }
                  });
                  classesToRemove.forEach((className) => node.classList.remove(className));
                }

                // Clean the delta ops to remove any inline style attributes that Quill might have converted
                if (delta && delta.ops) {
                  delta.ops = delta.ops.map((op: any) => {
                    if (op.attributes) {
                      const cleanAttrs: any = {};
                      // Only keep attributes that are in our ALLOWED_FORMATS
                      const allowedAttrs = ['bold', 'italic', 'underline', 'strike', 'link', 'color', 'size', 'weight', 'align', 'header', 'list'];
                      for (const key of Object.keys(op.attributes)) {
                        if (allowedAttrs.includes(key)) {
                          cleanAttrs[key] = op.attributes[key];
                        }
                      }
                      return { ...op, attributes: Object.keys(cleanAttrs).length > 0 ? cleanAttrs : undefined };
                    }
                    return op;
                  });
                }

                return delta;
              }],
            ],
          },
          keyboard: {
            bindings: {
              // Shift+Enter - line break
              linebreak: {
                key: 'Enter',
                shiftKey: true,
                handler: function(this: any, range: { index: number }) {
                  this.quill.insertEmbed(range.index, 'linebreak', true, 'user');
                  this.quill.setSelection(range.index + 1, 'silent');
                  return false;
                }
              }
            }
          },
        },
        formats: ALLOWED_FORMATS,
      });

      // Override table module's default Enter key behavior
      // Intercept Enter key at the DOM level before Quill processes it
      quill.root.addEventListener('keydown', function(e: KeyboardEvent) {
        if (e.key !== 'Enter') return;

        const selection = quill.getSelection();
        if (!selection) return;

        const tableModule = quill.getModule('table') as any;
        if (!tableModule) return;

        const [tableBlot] = tableModule.getTable(selection) || [];
        if (!tableBlot) return;

        // We're in a table - prevent default and stop all other listeners
        e.preventDefault();
        e.stopImmediatePropagation();

        if (e.shiftKey) {
          // Shift+Enter: insert single <br> + zero-width space cursor anchor
          const singleBr = new Delta()
            .retain(selection.index)
            .insert({ linebreak: true })
            .insert('\u200B');
          quill.updateContents(singleBr as any, 'user');
          // Position cursor after the ZWS (BR=1 + ZWS=1 = 2)
          const shiftTarget = selection.index + 2;
          setTimeout(() => quill.setSelection(shiftTarget, 0, 'silent'), 0);
        } else {
          // Enter: insert double <br> for paragraph spacing + ZWS cursor anchor
          const doubleBr = new Delta()
            .retain(selection.index)
            .insert({ linebreak: true })
            .insert({ linebreak: true })
            .insert('\u200B');
          quill.updateContents(doubleBr as any, 'user');
          // Position cursor after the ZWS (BR=1 + BR=1 + ZWS=1 = 3)
          const enterTarget = selection.index + 3;
          setTimeout(() => quill.setSelection(enterTarget, 0, 'silent'), 0);
        }
      }, true); // Use capture phase to intercept before Quill

      const toolbarModule = quill.getModule("toolbar") as any;
      if (toolbarModule?.container) {
        toolbarModule.container.setAttribute(
          "data-eb-quill-owner",
          instanceIdRef.current
        );

        // Fix dropdown closing issue - close pickers when an item is clicked
        const toolbarContainer = toolbarModule.container as HTMLElement;

	        const hrButton = toolbarContainer.querySelector<HTMLButtonElement>(".ql-hr");
	        if (hrButton) {
	          hrButton.title = "Insert Horizontal Rule";
	        }

        const pickers = toolbarContainer.querySelectorAll<HTMLElement>(".ql-picker");

        // Ensure all pickers are closed on initialization
        pickers.forEach((picker) => {
          picker.classList.remove("ql-expanded");
        });

        pickers.forEach((picker) => {
          const items = picker.querySelectorAll<HTMLElement>(".ql-picker-item");
          items.forEach((item) => {
            const handler = () => {
              // Special handling for "no-color" button - remove all text-* color classes
              if (item.getAttribute("data-value") === "no-color") {
                const selection = quill.getSelection();
                if (selection && selection.length > 0) {
                  // Get the editor content
                  const editorRoot = quill.root as HTMLElement;

                  // Find all spans with text-* color classes in the selection range
                  const walker = document.createTreeWalker(
                    editorRoot,
                    NodeFilter.SHOW_ELEMENT,
                    null
                  );

                  const spansToProcess: HTMLElement[] = [];
                  let node: Node | null;
                  while ((node = walker.nextNode())) {
                    const el = node as HTMLElement;
                    if (el.tagName === 'SPAN') {
                      const classList = Array.from(el.classList);
                      if (classList.some(cls => cls.startsWith('text-'))) {
                        spansToProcess.push(el);
                      }
                    }
                  }

                  // Remove text-* classes from spans and unwrap if no other classes remain
                  spansToProcess.forEach((span) => {
                    const classList = Array.from(span.classList);
                    classList.forEach((cls) => {
                      if (cls.startsWith('text-')) {
                        span.classList.remove(cls);
                      }
                    });

                    // If span has no classes left, unwrap it
                    if (span.classList.length === 0) {
                      while (span.firstChild) {
                        span.parentNode?.insertBefore(span.firstChild, span);
                      }
                      span.parentNode?.removeChild(span);
                    }
                  });

                  // Update Quill's internal state
                  quill.update('user');
                }
              }
              // Remove expanded class to close the picker
              picker.classList.remove("ql-expanded");
            };
            item.addEventListener("click", handler);
            pickerClickHandlersRef.current.set(item, handler);
          });

          // Highlight currently selected item when dropdown opens
          // Quill removes .ql-selected when dropdown opens, so we use our own class
          const pickerLabel = picker.querySelector<HTMLElement>(".ql-picker-label");
          if (pickerLabel) {
            const labelHandler = () => {
              // Get the current value from the picker label
              const currentValue = pickerLabel.getAttribute("data-value") || "";
              // Find and highlight the matching item
              items.forEach((item) => {
                const itemValue = item.getAttribute("data-value") || "";
                if (itemValue === currentValue) {
                  item.classList.add("eb-picker-selected");
                } else {
                  item.classList.remove("eb-picker-selected");
                }
              });
            };
            pickerLabel.addEventListener("click", labelHandler);
            pickerClickHandlersRef.current.set(pickerLabel, labelHandler);
          }
        });

        // Close pickers when clicking outside
        const handleClickOutside = (event: Event) => {
          const target = event.target as Node | null;
          pickers.forEach((picker) => {
            if (!target) return;
            // Check if click is outside the picker
            if (!picker.contains(target) && picker.classList.contains("ql-expanded")) {
              picker.classList.remove("ql-expanded");
            }
          });
        };

        const doc = toolbarContainer.ownerDocument;
        doc.addEventListener("mousedown", handleClickOutside, true);
        doc.addEventListener("touchstart", handleClickOutside, true);
        // Store the handler for cleanup
        (toolbarContainer as any).__clickOutsideHandler = handleClickOutside;

        // Custom link handler to show our tooltip with checkbox
        toolbarModule.addHandler("link", (value: boolean | string) => {
          // Always show the link dialog, never auto-remove
          const selection = quill.getSelection();
          if (selection) {
            let linkRange = { index: selection.index, length: selection.length };

            // If no text is selected (cursor only), try to find the entire link
            if (selection.length === 0) {
              const format = quill.getFormat(selection.index, 1);
              if (format.link) {
                // Find the start of the link by going backwards
                let startIndex = selection.index;
                while (startIndex > 0) {
                  const prevFormat = quill.getFormat(startIndex - 1, 1);
                  if (!prevFormat.link) break;
                  startIndex--;
                }

                // Find the end of the link by going forwards
                let endIndex = selection.index;
                const totalLength = quill.getLength();
                while (endIndex < totalLength) {
                  const nextFormat = quill.getFormat(endIndex, 1);
                  if (!nextFormat.link) break;
                  endIndex++;
                }

                linkRange = { index: startIndex, length: endIndex - startIndex };
              }
            }

            linkSelectionRef.current = linkRange;

            // Get the text content of the selection/link
            const text = quill.getText(linkRange.index, linkRange.length);
            setLinkText(text);

            // Check if there's an existing link
            const format = quill.getFormat(linkRange.index, linkRange.length || 1);

            if (format.link) {
              // Existing link - populate the form for editing
              if (typeof format.link === "string") {
                setLinkUrl(format.link);
                setLinkNewWindow(false);
              } else if (typeof format.link === "object") {
                const linkObj = format.link as { href?: string; target?: string };
                setLinkUrl(linkObj.href || "");
                setLinkNewWindow(linkObj.target === "_blank");
              }
            } else {
              // New link - clear the URL
              setLinkUrl("");
              setLinkNewWindow(false);
            }

            setLinkTooltipVisible(true);
            // Close table toolbar when link tooltip opens
            setTableToolbarState((prev) => (prev.visible ? { ...prev, visible: false } : prev));
          }
        });

        toolbarModule.addHandler("image", () => {
          const fallbackIndex = Math.max(quill.getLength() - 1, 0);
          const selection = quill.getSelection() ?? {
            index: fallbackIndex,
            length: 0,
          };
          imageSelectionRef.current = selection;
          setLinkTooltipVisible(false);
          // Close table toolbar when image modal opens
          setTableToolbarState((prev) => (prev.visible ? { ...prev, visible: false } : prev));
          setIsImageModalOpen(true);
        });

	        toolbarModule.addHandler("hr", () => {
	          const selection = quill.getSelection(true) ?? {
	            index: Math.max(quill.getLength() - 1, 0),
	            length: 0,
	          };

	          if (selection.length > 0) {
	            quill.deleteText(selection.index, selection.length, "user");
	          }

	          let insertIndex = selection.index;
	          if (insertIndex > 0 && quill.getText(insertIndex - 1, 1) !== "\n") {
	            quill.insertText(insertIndex, "\n", "user");
	            insertIndex += 1;
	          }

	          quill.insertEmbed(insertIndex, "hr", true, "user");
	          const nextIndex = insertIndex + 1;

	          if (quill.getText(nextIndex, 1) !== "\n") {
	            quill.insertText(nextIndex, "\n", "user");
	          }

	          quill.setSelection(nextIndex + 1, 0, "silent");
	          quill.focus({ preventScroll: true });
	        });

        toolbarModule.addHandler("bold", () => {
          applyInlineFormatPreservingColor(quill, "bold");
        });
        toolbarModule.addHandler("italic", () => {
          applyInlineFormatPreservingColor(quill, "italic");
        });
        toolbarModule.addHandler("underline", () => {
          applyInlineFormatPreservingColor(quill, "underline");
        });
        toolbarModule.addHandler("strike", () => {
          applyInlineFormatPreservingColor(quill, "strike");
        });

      }

      const repositionAddButton = () => {
        if (!containerRef.current) return;
        const bounds = containerRef.current.getBoundingClientRect();
        const targetButtons = getAddButtonWrappersInBounds(bounds);
        if (!targetButtons.length) return;
        const root = quill.root as HTMLElement;
        const lineHeight = parseFloat(getComputedStyle(root).lineHeight || "20") || 20;
        const targetY = bounds.top + root.scrollHeight - lineHeight / 2;
        targetButtons.forEach((wrapper) => {
          const rect = wrapper.getBoundingClientRect();
          const dy = targetY - (rect.top + rect.height / 2);
          wrapper.style.transform = `translateY(${dy}px)`;
        });
      };

      const updateEditorHeight = () => {
        if (!containerRef.current) return;
        if (resizeRafRef.current !== null) {
          cancelAnimationFrame(resizeRafRef.current);
        }
        resizeRafRef.current = requestAnimationFrame(() => {
          const root = quill.root as HTMLElement;
          const scrollHeight = root.scrollHeight;
          containerRef.current!.style.height = `${scrollHeight}px`;
          root.style.height = "auto";
          resizeRafRef.current = null;
          repositionAddButton();
        });
      };

      // Sync the size picker label with the format at the cursor.
      // Quill's built-in toolbar update doesn't reliably clear the picker's
      // data-value attribute when the cursor moves to unformatted text, so
      // the label keeps showing the last-applied size. We fix this by
      // listening to selection-change and manually driving the label's
      // data-value ourselves.
      const handleSelectionChange = (range: { index: number; length: number } | null) => {
        if (!range) return;
        const formats = quill.getFormat(range) as Record<string, any>;
        const sizeValue = typeof formats.size === "string" && formats.size ? formats.size : null;

        const toolbarEl = toolbarModule?.container as HTMLElement | undefined;
        if (!toolbarEl) return;

        const sizePicker = toolbarEl.querySelector<HTMLElement>(".ql-size");
        if (!sizePicker) return;

        const label = sizePicker.querySelector<HTMLElement>(".ql-picker-label");
        if (!label) return;

        if (sizeValue) {
          label.setAttribute("data-value", sizeValue);
        } else {
          label.removeAttribute("data-value");
        }
      };

      // Handle text changes
      const handleTextChange = (_delta: any, _oldDelta: any, _source: string) => {
        // Mark as editing to prevent external updates from overwriting
        isEditingRef.current = true;
        if (editingTimeoutRef.current) {
          clearTimeout(editingTimeoutRef.current);
        }
        // Reset editing flag after a short delay to allow form sync
        editingTimeoutRef.current = setTimeout(() => {
          isEditingRef.current = false;
          editingTimeoutRef.current = null;
        }, 500);

        const path = props.__easyblocks?.path;
        if (!path) return;

        const serialized = buildSerializedEditorValue(quill);
        if (serialized === lastSerializedRef.current) return;

        lastSerializedRef.current = serialized;
        changeEditorFormValue(path, serialized);
        updateEditorHeight();
      };

      const handleKeydown = (event: KeyboardEvent) => {
        const quillSelection = quill.getSelection();
        if ((event.ctrlKey || event.metaKey) && !event.altKey && !event.shiftKey) {
          const key = event.key.toLowerCase();
          if (key === "b" || key === "i" || key === "u") {
            event.preventDefault();
            event.stopPropagation();
            if (typeof event.stopImmediatePropagation === "function") {
              event.stopImmediatePropagation();
            }
            const formatMap: Record<string, "bold" | "italic" | "underline"> = {
              b: "bold",
              i: "italic",
              u: "underline",
            };
            applyInlineFormatPreservingColor(quill, formatMap[key]);
            return;
          }
        }
        if (event.key === "Backspace") {
          const rangeForBackspace = quillSelection ?? { index: 0, length: 0 };
          const formats = quill.getFormat(
            rangeForBackspace.index,
            Math.max(rangeForBackspace.length, 1)
          ) as Record<string, unknown>;
          const [line] = quill.getLine(rangeForBackspace.index);
          const lineNode = line?.domNode as HTMLElement | undefined;

          const normalizedLineText = (lineNode?.textContent ?? "")
            .replace(/\u200B/g, "")
            .replace(/\uFEFF/g, "")
            .trim();
          const isAtCollapsedCursor = rangeForBackspace.length === 0;
          const isEmptyListItem =
            isAtCollapsedCursor &&
            lineNode?.tagName === "LI" &&
            Boolean(formats.list) &&
            normalizedLineText.length === 0;

          if (isEmptyListItem) {
            event.preventDefault();
            event.stopPropagation();
            if (typeof event.stopImmediatePropagation === "function") {
              event.stopImmediatePropagation();
            }
            quill.formatLine(rangeForBackspace.index, 1, "list", false, "user");
            quill.setSelection(rangeForBackspace.index, 0, "silent");
            return;
          }
        }
        if (event.key === "Backspace" || event.key === "Delete") {
          const range = quillSelection ?? { index: 0, length: 0 };
          let targetIndex: number | null = null;
          if (range.length === 1) {
            targetIndex = range.index;
          } else if (range.length === 0) {
            targetIndex =
              event.key === "Backspace" ? range.index - 1 : range.index;
          }

          if (targetIndex !== null && targetIndex >= 0) {
            const deleted = deleteImageAtIndex(targetIndex);
            if (deleted) {
              event.preventDefault();
              event.stopPropagation();
              if (typeof event.stopImmediatePropagation === "function") {
                event.stopImmediatePropagation();
              }
              setFloatingImageToolbarState({
                visible: false,
                top: 0,
                left: 0,
                style: "",
                alt: "",
                width: "",
                height: "",
                src: "",
                isExternal: false,
                index: null,
              });
              if (selectedImageNodeRef.current) {
                selectedImageNodeRef.current.classList.remove(
                  "eb-QuillText__image-selected"
                );
                selectedImageNodeRef.current = null;
              }
              lastKnownImageIndexRef.current = null;
              requestAnimationFrame(syncImageStyleControlState);
              return;
            }
          }
        }
        stopEditorShortcutPropagation(event);
      };
      const handleClipboardEvent = (event: ClipboardEvent) => {
        event.stopPropagation();
      };
      const handlePaste = (event: ClipboardEvent) => {
        event.stopPropagation();
        // After paste, strip all inline styles from the editor content
        setTimeout(() => {
          if (!quill) return;
          const editorElement = quill.root;
          const allElements = editorElement.querySelectorAll('[style]');
          allElements.forEach((el) => {
            el.removeAttribute('style');
          });
        }, 0);
      };
      const handleEditorPointerDown = (event: Event) => {
        // Prevent parent drag/drag-handle behavior when selecting text.
        event.stopPropagation();
      };
      const handleEditorClick = (event: Event) => {
        const target = event.target as HTMLElement | null;
        if (!target || target.tagName !== "IMG") return;
        const blot = Quill.find(target);
        if (!blot) return;
        const index = quill.getIndex(blot as any);
        lastKnownImageIndexRef.current = index;
        quill.focus({ preventScroll: true });
        quill.setSelection(index, 1, "user");
        requestAnimationFrame(() => {
          syncImageStyleControlState();
        });
      };

      const touchStartOptions: AddEventListenerOptions = {
        capture: true,
        passive: true
      };
      quill.on("selection-change", handleSelectionChange);
      quill.on("text-change", handleTextChange);
      quill.root.addEventListener("keydown", handleKeydown, true);
      quill.root.addEventListener("cut", handleClipboardEvent, true);
      quill.root.addEventListener("copy", handleClipboardEvent, true);
      quill.root.addEventListener("paste", handlePaste, true);
      quill.root.addEventListener("pointerdown", handleEditorPointerDown, true);
      quill.root.addEventListener("mousedown", handleEditorPointerDown, true);
      quill.root.addEventListener("click", handleEditorClick, true);
      quill.root.addEventListener(
        "touchstart",
        handleEditorPointerDown,
        touchStartOptions
      );
      quillRef.current = quill;
      updateEditorHeight();

      // Cleanup
      return () => {
        if (resizeRafRef.current !== null) {
          cancelAnimationFrame(resizeRafRef.current);
          resizeRafRef.current = null;
        }
        if (editingTimeoutRef.current) {
          clearTimeout(editingTimeoutRef.current);
          editingTimeoutRef.current = null;
        }
        isEditingRef.current = false;
        if (quillRef.current) {
          quillRef.current.off("selection-change", handleSelectionChange);
          quillRef.current.off("text-change", handleTextChange);
          quillRef.current.root.removeEventListener("keydown", handleKeydown, true);
          quillRef.current.root.removeEventListener("cut", handleClipboardEvent, true);
          quillRef.current.root.removeEventListener("copy", handleClipboardEvent, true);
          quillRef.current.root.removeEventListener("paste", handlePaste, true);
          quillRef.current.root.removeEventListener(
            "pointerdown",
            handleEditorPointerDown,
            true
          );
          quillRef.current.root.removeEventListener(
            "mousedown",
            handleEditorPointerDown,
            true
          );
          quillRef.current.root.removeEventListener("click", handleEditorClick, true);
          quillRef.current.root.removeEventListener(
            "touchstart",
            handleEditorPointerDown,
            touchStartOptions
          );
          quillRef.current = null;
        }
        // Clean up picker click handlers
        pickerClickHandlersRef.current.forEach((handler, item) => {
          item.removeEventListener("click", handler);
        });
        pickerClickHandlersRef.current.clear();
        
        // Clean up click outside handler
        const toolbarEl = toolbarModule?.container as HTMLElement | undefined;
        if (toolbarEl) {
          const clickOutsideHandler = (toolbarEl as any).__clickOutsideHandler;
          if (clickOutsideHandler) {
            const doc = toolbarEl.ownerDocument;
            doc.removeEventListener("mousedown", clickOutsideHandler, true);
            doc.removeEventListener("touchstart", clickOutsideHandler, true);
            delete (toolbarEl as any).__clickOutsideHandler;
          }
        }
        
        if (toolbarEl?.parentElement) {
          toolbarEl.parentElement.removeChild(toolbarEl);
        }
        if (containerRef.current) {
          containerRef.current.innerHTML = "";
          containerRef.current.style.height = "";
        }
        if (selectedImageNodeRef.current) {
          selectedImageNodeRef.current.classList.remove(
            "eb-QuillText__image-selected"
          );
          selectedImageNodeRef.current = null;
        }
      };
  }, [
    props.__easyblocks?.path,
    syncImageStyleControlState,
    applyInlineFormatPreservingColor,
  ]);

  const handlePickImage = useCallback((url: string) => {
    const cleanUrl = String(url ?? "").trim();
    const quill = quillRef.current;
    if (!cleanUrl || !quill) {
      setIsImageModalOpen(false);
      imageSelectionRef.current = null;
      return;
    }

    const fallbackIndex = Math.max(quill.getLength() - 1, 0);
    const selection = imageSelectionRef.current ?? quill.getSelection() ?? {
      index: fallbackIndex,
      length: 0,
    };
    const index = Math.max(selection.index, 0);

    if (selection.length > 0) {
      quill.deleteText(index, selection.length, "user");
    }
    quill.insertEmbed(index, "image", cleanUrl, "user");
    // Apply default "centered" style to new images
    quill.setSelection(index, 1, "silent");
    quill.format("imageStyle", "centered", "user");
    lastKnownImageIndexRef.current = index;
    quill.focus({ preventScroll: true });
    quill.setSelection(index, 1, "silent");
    requestAnimationFrame(syncImageStyleControlState);

    setIsImageModalOpen(false);
    imageSelectionRef.current = null;
  }, [syncImageStyleControlState]);

  const handleCloseImageModal = useCallback(() => {
    setIsImageModalOpen(false);
    imageSelectionRef.current = null;
  }, []);

  // Sync external value changes to editor
  useEffect(() => {
    const quill = quillRef.current;
    if (!quill || isHtmlMode) {
      return;
    }

    // Skip if we're currently editing (e.g., just applied HTML changes or user is typing)
    // This prevents external/stale values from overwriting user edits
    if (isEditingRef.current) {
      return;
    }

    const rawValue = typeof currentValue === "string" ? currentValue : "";

    // Avoid unnecessary updates
    if (rawValue === lastSerializedRef.current) {
      return;
    }

    // Parse as new format {delta, html} - no legacy support
    const contentToRestore = parseSerializedValue(rawValue);

    if (contentToRestore?.delta) {
      const hasTables =
        serializedHtmlContainsTables(contentToRestore.html) ||
        serializedDeltaContainsTables(contentToRestore.delta);
      const deltaToRestore = hasTables
        ? contentToRestore.delta
        : normalizePlainParagraphSpacingInDelta(contentToRestore.delta);

      // Prefer delta for accurate format restoration
      quill.setContents(deltaToRestore as any, "silent");
      const styledColorSegments = extractStyledColorSegmentsFromDelta(
        deltaToRestore
      );
      if (styledColorSegments.length > 0) {
        for (const segment of styledColorSegments) {
          quill.formatText(
            segment.index,
            segment.length,
            "color",
            segment.color,
            "silent"
          );
        }
      }
      // Pass saved HTML to restore cell alignment classes
      syncAllTableClasses(quill, contentToRestore.html);
      lastSerializedRef.current = rawValue;
    } else if (contentToRestore?.html) {
      // Use HTML with clipboard.convert when delta is unavailable
      const convertedDelta = quill.clipboard.convert({ html: contentToRestore.html });
      quill.setContents(convertedDelta, "silent");
      syncAllTableClasses(quill, contentToRestore.html);
      lastSerializedRef.current = rawValue;
    } else {
      quill.setText("", "silent");
      lastSerializedRef.current = "";
    }
    if (containerRef.current) {
      const syncLayout = () => {
        if (!containerRef.current) return;
        const root = quill.root as HTMLElement;
        containerRef.current.style.height = `${root.scrollHeight}px`;
        root.style.height = "auto";
        const bounds = containerRef.current.getBoundingClientRect();
        const targetButtons = getAddButtonWrappersInBounds(bounds);
        if (targetButtons.length) {
          const lineHeight = parseFloat(getComputedStyle(root).lineHeight || "20") || 20;
          const targetY = bounds.top + root.scrollHeight - lineHeight / 2;
          targetButtons.forEach((wrapper) => {
            const rect = wrapper.getBoundingClientRect();
            const dy = targetY - (rect.top + rect.height / 2);
            wrapper.style.transform = `translateY(${dy}px)`;
          });
        }
      };

      // Defer to next frame to ensure layout is updated for large content
      requestAnimationFrame(syncLayout);
    }
  }, [currentValue, isHtmlMode]);

  // Get current HTML for the HTML view - use Quill's native HTML output
  const getCurrentHtml = useCallback(() => {
    const quill = quillRef.current;
    if (quill) {
      return quill.root.innerHTML;
    }
    // Fallback to stored html/delta if Quill not available
    const rawValue = typeof currentValue === "string" ? currentValue : "";
    const parsed = parseSerializedValue(rawValue);
    if (parsed?.html) {
      return parsed.html;
    }
    if (parsed?.delta) {
      return renderQuillDeltaToHtml(JSON.stringify(parsed.delta));
    }
    return "";
  }, [currentValue]);

  // Handle switching to HTML mode
  const handleToggleHtmlMode = useCallback(() => {
    const quill = quillRef.current;
    if (!quill) return;

    if (isHtmlMode) {
      // Set editing flag FIRST to prevent form subscription from overwriting our changes
      isEditingRef.current = true;

      // Switching from HTML mode back to WYSIWYG
      // Read directly from DOM ref to avoid stale closure issue with state
      const refValue = htmlTextareaRef.current?.value;
      const editedHtml = refValue ?? htmlContent;

      // Set innerHTML directly to preserve exact HTML (including Bootstrap classes)
      // then call update() to sync Quill's internal state
      quill.root.innerHTML = editedHtml;
      quill.update('user');

        // Trigger form update - now getContents() will return the correct delta
        const path = props.__easyblocks?.path;
        if (path) {
          persistEditorValue(quill);
        }

        // Update editor height after content change and restore Quill classes
      requestAnimationFrame(() => {
        if (containerRef.current) {
          // Re-add Quill classes that may have been lost during HTML mode switch
          containerRef.current.classList.add('ql-container', 'ql-snow');
          const root = quill.root as HTMLElement;
          containerRef.current.style.height = `${root.scrollHeight}px`;
          root.style.height = "auto";
        }
      });
      // First switch back to editor mode
      setIsHtmlMode(false);
      // Delay clearing the editing flag to allow form change to propagate
      // This prevents the form subscription from overwriting our changes with stale data
      setTimeout(() => {
        isEditingRef.current = false;
      }, 100);
    } else {
      // Switching to HTML mode - set editing flag to prevent external updates
      // Clear any pending timeout that would reset the editing flag
      if (editingTimeoutRef.current) {
        clearTimeout(editingTimeoutRef.current);
        editingTimeoutRef.current = null;
      }
      isEditingRef.current = true;
      setHtmlContent(getCurrentHtml());
      setIsHtmlMode(true);
    }
  }, [isHtmlMode, htmlContent, props.__easyblocks?.path, getCurrentHtml]);

  // Add HTML toggle button to toolbar after Quill initializes
  useEffect(() => {
    const quill = quillRef.current;
    if (!quill) return;

    const toolbarModule = quill.getModule("toolbar") as any;
    const toolbarContainer = toolbarModule?.container as HTMLElement | undefined;
    if (!toolbarContainer) return;

    // Check if button already exists
    if (toolbarContainer.querySelector(".ql-html-toggle")) return;

    // Create HTML toggle button
    const htmlButton = document.createElement("button");
    htmlButton.type = "button";
    htmlButton.className = "ql-html-toggle";
    htmlButton.innerHTML = "&lt;/&gt;";
    htmlButton.title = "Toggle HTML View";
    htmlButton.style.cssText = "font-size: 12px; font-weight: bold; padding: 0 8px; width: min-content;";

    const handleClick = () => handleToggleHtmlMode();
    htmlButton.addEventListener("click", handleClick);

    // Add to toolbar
    const formatGroup = document.createElement("span");
    formatGroup.className = "ql-formats";
    formatGroup.appendChild(htmlButton);
    toolbarContainer.appendChild(formatGroup);

    return () => {
      htmlButton.removeEventListener("click", handleClick);
      formatGroup.remove();
    };
  }, [handleToggleHtmlMode]);

  // Update button state when mode changes
  useEffect(() => {
    const quill = quillRef.current;
    if (!quill) return;

    const toolbarModule = quill.getModule("toolbar") as any;
    const toolbarContainer = toolbarModule?.container as HTMLElement | undefined;
    if (!toolbarContainer) return;

    const htmlButton = toolbarContainer.querySelector(".ql-html-toggle") as HTMLElement | null;
    if (htmlButton) {
      htmlButton.classList.toggle("ql-active", isHtmlMode);
    }
  }, [isHtmlMode]);

  // --- Table style helpers ---

  /** Apply styles to every cell in a table (updates row IDs in delta) and sync DOM classes */
  const applyTableStyles = useCallback((quill: Quill, tableBlot: any, newStyles: string[]) => {
    const rows = tableBlot.rows?.() || [];
    for (const row of rows) {
      let cell = row.children?.head;
      while (cell) {
        const cellFormats = cell.formats?.() || {};
        const rowId = cellFormats.table || "";
        const parsed = parseTableRowId(rowId);
        const newId = buildTableRowId(parsed.base, newStyles);
        if (rowId !== newId) {
          cell.format("table", newId);
        }
        cell = cell.next;
      }
    }
    syncTableDomClasses(tableBlot, newStyles);
    quill.update("user" as any);
  }, []);

  /** Sync Bootstrap classes on the <table> DOM element for editor preview */
  const syncTableDomClasses = useCallback((tableBlot: any, styles: string[]) => {
    const tableDom = tableBlot?.domNode as HTMLElement | null;
    if (!tableDom) return;
    const classes = ["table", ...styles.map((s) => {
      const found = BOOTSTRAP_TABLE_STYLES.find((bs) => bs.key === s);
      return found ? found.cls : "";
    }).filter(Boolean)];
    tableDom.className = classes.join(" ");
  }, []);

  /** After setContents, apply Bootstrap classes to all table DOM elements from encoded row IDs */
  const syncAllTableClasses = useCallback((quill: Quill, savedHtml?: string) => {
    const tables = quill.root.querySelectorAll<HTMLElement>("table");
    tables.forEach((tableDom) => {
      const firstCell = tableDom.querySelector<HTMLElement>("[data-row]");
      const rowId = firstCell?.getAttribute("data-row") ?? "";
      const { styles } = parseTableRowId(rowId);
      const classes = ["table", ...styles.map((s) => {
        const found = BOOTSTRAP_TABLE_STYLES.find((bs) => bs.key === s);
        return found ? found.cls : "";
      }).filter(Boolean)];
      tableDom.className = classes.join(" ");
    });

    // If we have saved HTML, restore cell alignment classes from it
    if (savedHtml) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(savedHtml, 'text/html');
      const savedCells = doc.querySelectorAll<HTMLElement>('td, th');
      const editorCells = quill.root.querySelectorAll<HTMLElement>('td, th');

      // Match cells by position and restore alignment classes
      savedCells.forEach((savedCell, index) => {
        const editorCell = editorCells[index];
        if (editorCell) {
          // Restore horizontal alignment classes
          const hAlignClasses = ['text-start', 'text-center', 'text-end'];
          hAlignClasses.forEach(cls => {
            if (savedCell.classList.contains(cls)) {
              editorCell.classList.add(cls);
            }
          });

          // Restore vertical alignment classes
          const vAlignClasses = ['align-top', 'align-middle', 'align-bottom'];
          vAlignClasses.forEach(cls => {
            if (savedCell.classList.contains(cls)) {
              editorCell.classList.add(cls);
            }
          });
        }
      });
    }

    // Fix table structure to ensure proper thead/tbody
    fixEditorTableStructure(quill.root);
  }, []);

  /** Toggle a single style on the current table */
  const toggleTableStyle = useCallback((styleKey: string) => {
    const quill = quillRef.current;
    if (!quill) return;
    const tableModule = quill.getModule("table") as any;
    if (!tableModule) return;
    const range = quill.getSelection();
    if (!range) return;
    const [tableBlot] = tableModule.getTable(range) || [];
    if (!tableBlot) return;

    let currentStyles = [...tableToolbarState.styles];

    // Check if this is a border style (mutually exclusive)
    const isBorderStyle = BOOTSTRAP_TABLE_BORDER_STYLES.some(s => s.key === styleKey);

    if (isBorderStyle) {
      // Remove all other border styles
      currentStyles = currentStyles.filter(s =>
        !BOOTSTRAP_TABLE_BORDER_STYLES.some(bs => bs.key === s)
      );
      // Add the new border style (unless it's "default")
      if (styleKey !== "default") {
        currentStyles.push(styleKey);
      }
    } else {
      // For non-border styles, toggle normally
      const idx = currentStyles.indexOf(styleKey);
      if (idx >= 0) {
        currentStyles.splice(idx, 1);
      } else {
        currentStyles.push(styleKey);
      }
    }

    applyTableStyles(quill, tableBlot, currentStyles);
    setTableToolbarState((prev) => ({ ...prev, styles: currentStyles }));
  }, [tableToolbarState.styles, applyTableStyles]);

  /** Toggle the first row between <th> (table-header) and <td> (table) */
  const toggleTableHeader = useCallback(() => {
    const quill = quillRef.current;
    if (!quill) return;
    const tableModule = quill.getModule("table") as any;
    if (!tableModule) return;
    const range = quill.getSelection();
    if (!range) return;
    const [tableBlot] = tableModule.getTable(range) || [];
    if (!tableBlot) return;

    const rows = tableBlot.rows?.() || [];
    if (rows.length === 0) return;
    const firstRow = rows[0];

    // Check current state from first cell using hdr- prefix
    const firstCell = firstRow.children?.head;
    const firstFormats = firstCell?.formats?.() || {};
    const firstRowId = firstFormats.table || "";
    const isCurrentlyHeader = parseTableRowId(firstRowId).isHeader;

    // Collect cells first (replaceWith invalidates .next pointers)
    const cells: any[] = [];
    let c = firstRow.children?.head;
    while (c) {
      cells.push(c);
      c = c.next;
    }

    // Toggle by swapping hdr-/row- prefix; always use the 'table' blot
    for (const cell of cells) {
      const cellFormats = cell.formats?.() || {};
      const rowId = cellFormats.table || "";
      const p = parseTableRowId(rowId);
      const newBase = isCurrentlyHeader
        ? p.base.replace(/^hdr-/, "row-")
        : p.base.replace(/^row-/, "hdr-");
      const newId = buildTableRowId(newBase, p.styles);
      cell.replaceWith("table", newId);
    }

    quill.update("user" as any);

    // Fix table structure to ensure th cells are in thead
    fixEditorTableStructure(quill.root);

    setTableToolbarState((prev) => ({ ...prev, hasHeader: !isCurrentlyHeader }));
  }, []);

  /** Set horizontal alignment for current table cell(s) */
  const setCellHAlign = useCallback((align: string) => {
    const quill = quillRef.current;
    if (!quill) return;

    const cells = currentTableCellsRef.current;
    if (!cells || cells.length === 0) return;

    // Apply alignment to all selected cells
    cells.forEach((cellNode) => {
      // Remove existing horizontal alignment classes
      cellNode.classList.remove("text-start", "text-center", "text-end");

      // Add new alignment class if not empty
      if (align) {
        cellNode.classList.add(`text-${align}`);
      }
    });

    // Notify Quill of the DOM change
    quill.update("user" as any);

    // Manually trigger save since DOM-only changes don't trigger text-change event
    const serialized = persistEditorValue(quill, postProcessQuillHtml(quill.root.innerHTML));
    lastSerializedRef.current = serialized;

    setTableToolbarState((prev) => ({ ...prev, cellHAlign: align }));
  }, [persistEditorValue]);

  /** Set vertical alignment for current table cell(s) */
  const setCellVAlign = useCallback((align: string) => {
    const quill = quillRef.current;
    if (!quill) return;

    const cells = currentTableCellsRef.current;
    if (!cells || cells.length === 0) return;

    // Apply alignment to all selected cells
    cells.forEach((cellNode) => {
      // Remove existing vertical alignment classes
      cellNode.classList.remove("align-top", "align-middle", "align-bottom");

      // Add new alignment class if not empty
      if (align) {
        cellNode.classList.add(`align-${align}`);
      }
    });

    // Notify Quill of the DOM change
    quill.update("user" as any);

    // Manually trigger save since DOM-only changes don't trigger text-change event
    const serialized = persistEditorValue(quill, postProcessQuillHtml(quill.root.innerHTML));
    lastSerializedRef.current = serialized;

    setTableToolbarState((prev) => ({ ...prev, cellVAlign: align }));
  }, [persistEditorValue]);

  // Add table insert button to toolbar
  useEffect(() => {
    const quill = quillRef.current;
    if (!quill) return;

    const toolbarModule = quill.getModule("toolbar") as any;
    const toolbarContainer = toolbarModule?.container as HTMLElement | undefined;
    if (!toolbarContainer) return;

    if (toolbarContainer.querySelector(".ql-insert-table")) return;

    const tableButton = document.createElement("button");
    tableButton.type = "button";
    tableButton.className = "ql-insert-table";
    tableButton.innerHTML = `<svg viewBox="0 0 18 18">
      <rect class="ql-stroke" height="14" width="14" x="2" y="2" fill="none"></rect>
      <line class="ql-stroke" x1="2" y1="7" x2="16" y2="7"></line>
      <line class="ql-stroke" x1="2" y1="11" x2="16" y2="11"></line>
      <line class="ql-stroke" x1="9" y1="2" x2="9" y2="16"></line>
    </svg>`;
    tableButton.title = "Insert Table";

    const handleClick = () => {
      const tableModule = quill.getModule("table") as any;
      if (tableModule) {
        tableModule.insertTable(3, 3);
        // Apply default style (no border classes) to newly created table
        setTimeout(() => {
          const range = quill.getSelection();
          if (!range) return;
          const [tbl] = tableModule.getTable(range) || [];
          if (!tbl) return;
          applyTableStyles(quill, tbl, []);
          fixEditorTableStructure(quill.root);
        }, 0);
      }
    };
    tableButton.addEventListener("click", handleClick);

    const formatGroup = document.createElement("span");
    formatGroup.className = "ql-formats";
    formatGroup.appendChild(tableButton);
    // Insert before the last group (HTML toggle) so table button is near the end
    const htmlToggleGroup = toolbarContainer.querySelector(".ql-html-toggle")?.parentElement;
    if (htmlToggleGroup) {
      toolbarContainer.insertBefore(formatGroup, htmlToggleGroup);
    } else {
      toolbarContainer.appendChild(formatGroup);
    }

    return () => {
      tableButton.removeEventListener("click", handleClick);
      formatGroup.remove();
    };
  }, []);

  // Show/hide table context toolbar on selection change
  useEffect(() => {
    const quill = quillRef.current;
    if (!quill) return;

    const handleSelectionChange = () => {
      const tableModule = quill.getModule("table") as any;
      if (!tableModule) return;

      const range = quill.getSelection();
      if (!range) {
        setTableToolbarState((prev) => (prev.visible ? { ...prev, visible: false } : prev));
        return;
      }

      const [tableData] = tableModule.getTable(range) || [];
      if (!tableData) {
        setTableToolbarState((prev) => (prev.visible ? { ...prev, visible: false } : prev));
        return;
      }

      // Cursor is inside a table cell — show the toolbar
      const tableDom = tableData.domNode?.closest?.("table") || tableData.domNode;
      if (!tableDom || !wrapperRef.current) {
        setTableToolbarState((prev) => (prev.visible ? { ...prev, visible: false } : prev));
        return;
      }

      // Read styles and header info from first cell's row ID
      const rows = tableData.rows?.() || [];
      let styles: string[] = [];
      let hasHeader = false;
      if (rows.length > 0) {
        const firstRow = rows[0];
        const firstCell = firstRow?.children?.head;
        if (firstCell) {
          const cellFormats = firstCell.formats?.() || {};
          const rowId = cellFormats.table || "";
          const parsed = parseTableRowId(rowId);
          hasHeader = parsed.isHeader;
          styles = parsed.styles;
        }
      }

      // Find all selected cells in the range
      const selectedCells = new Set<HTMLElement>();
      let cellHAlign = "";
      let cellVAlign = "";

      // Iterate through the selection range to find all cells
      for (let i = range.index; i < range.index + range.length; i++) {
        const [leaf] = quill.getLeaf(i);
        if (leaf && leaf.domNode) {
          // Get the parent element if domNode is a text node
          const node = leaf.domNode as Node;
          const element =
            node.nodeType === Node.TEXT_NODE
              ? (node.parentElement as Element | null)
              : (node as Element);
          if (!element) continue;
          const cellNode = element.closest("td, th") as HTMLElement | null;
          if (cellNode) {
            selectedCells.add(cellNode);
          }
        }
      }

      // If no cells found in range, try to get the cell at cursor position
      if (selectedCells.size === 0) {
        const [leaf] = quill.getLeaf(range.index);
        if (leaf && leaf.domNode) {
          const node = leaf.domNode as Node;
          const element =
            node.nodeType === Node.TEXT_NODE
              ? (node.parentElement as Element | null)
              : (node as Element);
          if (element) {
            const cellNode = element.closest("td, th") as HTMLElement | null;
            if (cellNode) {
              selectedCells.add(cellNode);
            }
          }
        }
      }

      // Store all selected cells
      currentTableCellsRef.current = Array.from(selectedCells);

      // Read alignment from the first cell to show in toolbar
      if (currentTableCellsRef.current.length > 0) {
        const firstCell = currentTableCellsRef.current[0];

        // Read horizontal alignment from text-* classes
        if (firstCell.classList.contains("text-start")) cellHAlign = "start";
        else if (firstCell.classList.contains("text-center")) cellHAlign = "center";
        else if (firstCell.classList.contains("text-end")) cellHAlign = "end";

        // Read vertical alignment from align-* classes
        if (firstCell.classList.contains("align-top")) cellVAlign = "top";
        else if (firstCell.classList.contains("align-middle")) cellVAlign = "middle";
        else if (firstCell.classList.contains("align-bottom")) cellVAlign = "bottom";
      }

      const tableRect = tableDom.getBoundingClientRect();

      // Toolbar is position: fixed, so coordinates are in viewport space.
      // The Quill editor itself has max-height: 60vh and scrolls internally,
      // so the table's DOM rect can extend far past the visible editor area.
      // Clamp the table anchor to .ql-editor's visible rect so the toolbar
      // stays attached to the editor, not the off-screen rendered table.
      const editorEl = quill.root;
      const editorRect = editorEl.getBoundingClientRect();
      const anchorBottom = Math.min(tableRect.bottom, editorRect.bottom);
      const anchorTop = Math.max(tableRect.top, editorRect.top);
      const anchorLeft = Math.max(tableRect.left, editorRect.left);

      const toolbarHeight = floatingTableToolbarRef.current?.offsetHeight ?? 100;
      const margin = 4;
      const viewportHeight = window.innerHeight;

      // The Quill formatting toolbar can scroll with the page — read its
      // live viewport rect so we never overlap it regardless of scroll state.
      const qlToolbarEl = wrapperRef.current.querySelector(".ql-toolbar") as HTMLElement | null;
      const qlToolbarRect = qlToolbarEl?.getBoundingClientRect();
      const minTop = qlToolbarRect ? qlToolbarRect.bottom + margin : margin;

      let top: number;
      if (anchorBottom + margin + toolbarHeight <= viewportHeight) {
        top = anchorBottom + margin;
      } else if (anchorTop - margin - toolbarHeight >= minTop) {
        top = anchorTop - margin - toolbarHeight;
      } else {
        top = viewportHeight - toolbarHeight - 8;
      }
      top = Math.max(top, minTop);

      setTableToolbarState({
        visible: true,
        top,
        left: anchorLeft,
        styles,
        hasHeader,
        cellHAlign,
        cellVAlign,
      });
    };

    quill.on("selection-change", handleSelectionChange);
    quill.on("text-change", handleSelectionChange);

    // Keep the toolbar positioned correctly as the page scrolls or resizes.
    // capture: true catches scrolls on any ancestor (page, section, editor).
    window.addEventListener("scroll", handleSelectionChange, true);
    window.addEventListener("resize", handleSelectionChange);

    return () => {
      quill.off("selection-change", handleSelectionChange);
      quill.off("text-change", handleSelectionChange);
      window.removeEventListener("scroll", handleSelectionChange, true);
      window.removeEventListener("resize", handleSelectionChange);
    };
  }, []);

  useEffect(() => {
    // Keep scroll listener active even when toolbar is hidden, so it can reappear when image scrolls back into view
    const refreshPosition = () => syncImageStyleControlState();
    window.addEventListener("resize", refreshPosition);
    window.addEventListener("scroll", refreshPosition, true);
    return () => {
      window.removeEventListener("resize", refreshPosition);
      window.removeEventListener("scroll", refreshPosition, true);
    };
  }, [syncImageStyleControlState]);

  useAddButtonsVisibility({
    tableToolbarVisible: tableToolbarState.visible,
    imageToolbarVisible: floatingImageToolbarState.visible,
    linkTooltipVisible,
    imageModalOpen: isImageModalOpen,
  });

  // Keep image selection state and floating toolbar synced with editor changes.
  useEffect(() => {
    const quill = quillRef.current;
    if (!quill) return;
    const handleSelectionOrContentChange = () => syncImageStyleControlState();
    quill.on("selection-change", handleSelectionOrContentChange);
    quill.on("text-change", handleSelectionOrContentChange);
    return () => {
      quill.off("selection-change", handleSelectionOrContentChange);
      quill.off("text-change", handleSelectionOrContentChange);
    };
  }, [syncImageStyleControlState]);



  const handleSaveLink = useCallback(() => {
    if (!linkUrl || !linkText || !linkSelectionRef.current || !quillRef.current) return;
    const quill = quillRef.current;
    const range = linkSelectionRef.current;
    let finalUrl = linkUrl.trim();
    if (finalUrl && !finalUrl.includes(":") && finalUrl.includes("@")) {
      finalUrl = `mailto:${finalUrl}`;
    }
    quill.deleteText(range.index, range.length, "user");
    quill.insertText(range.index, linkText, "user");
    quill.setSelection(range.index, linkText.length, "user");
    const linkValue = linkNewWindow ? { href: finalUrl, target: "_blank" } : finalUrl;
    quill.format("link", linkValue);
    setLinkTooltipVisible(false);
  }, [linkUrl, linkText, linkNewWindow]);

  const handleUnlink = useCallback(() => {
    if (linkSelectionRef.current && quillRef.current) {
      const quill = quillRef.current;
      const range = linkSelectionRef.current;
      quill.setSelection(range.index, range.length);
      quill.format("link", false);
    }
    setLinkTooltipVisible(false);
  }, []);

  const handleDeleteImage = useCallback(() => {
    const quill = quillRef.current;
    if (!quill || floatingImageToolbarState.index === null) return;
    deleteImageAtIndex(floatingImageToolbarState.index);
    setFloatingImageToolbarState({
      visible: false,
      top: 0,
      left: 0,
      style: "",
      alt: "",
      width: "",
      height: "",
      src: "",
      isExternal: false,
      index: null,
    });
    if (selectedImageNodeRef.current) {
      selectedImageNodeRef.current.classList.remove("eb-QuillText__image-selected");
      selectedImageNodeRef.current = null;
    }
    lastKnownImageIndexRef.current = null;
  }, [deleteImageAtIndex, floatingImageToolbarState.index]);

  const wrapperClassName = [
    "eb-QuillText__wrapper",
    isHtmlMode ? "eb-QuillText__wrapper--html-mode" : "",
  ].filter(Boolean).join(" ");

  return (
    <div className={wrapperClassName} data-eb-block="QuillText" ref={wrapperRef}>
      {/* Always render Quill container to keep it mounted */}
      <div
        className={`eb-QuillText eb-QuillText--editor${isHtmlMode ? " eb-QuillText--hidden" : ""}`}
        ref={containerRef}
      />
      {/* HTML mode overlay */}
      {isHtmlMode && (
        <div className="eb-QuillText eb-QuillText--editor eb-QuillText--html-mode">
          <div className="eb-QuillText__html-toolbar">
            <button
              type="button"
              className="eb-QuillText__html-back-btn"
              onClick={handleToggleHtmlMode}
            >
              ← Back to Editor
            </button>
            <span className="eb-QuillText__html-label">HTML View</span>
          </div>
          <textarea
            ref={htmlTextareaRef}
            className="eb-QuillText__html-textarea"
            value={htmlContent}
            onChange={(e) => setHtmlContent(e.target.value)}
            onKeyDown={(e) => stopEditorShortcutPropagation(e)}
            onMouseDown={(e) => e.stopPropagation()}
            onDragStart={(e) => e.stopPropagation()}
            spellCheck={false}
          />
        </div>
      )}
      <LinkTooltip
        visible={linkTooltipVisible}
        linkTooltipRef={linkTooltipRef}
        linkUrl={linkUrl}
        linkText={linkText}
        linkNewWindow={linkNewWindow}
        onChangeUrl={setLinkUrl}
        onChangeText={setLinkText}
        onChangeNewWindow={setLinkNewWindow}
        onSave={handleSaveLink}
        onUnlink={handleUnlink}
        onCancel={() => setLinkTooltipVisible(false)}
        stopEditorShortcutPropagation={stopEditorShortcutPropagation}
      />
      <ImageToolbar
        state={floatingImageToolbarState}
        isHtmlMode={isHtmlMode}
        isImageModalOpen={isImageModalOpen}
        toolbarRef={floatingImageToolbarRef}
        onApplyStyle={applyImageStyleAtSelection}
        onApplyAlt={(alt) => applyImageMetadataAtSelection({ alt })}
        onDeleteImage={handleDeleteImage}
        onDraftAltChange={(alt) =>
          setFloatingImageToolbarState((prev) => ({ ...prev, alt }))
        }
        stopEditorShortcutPropagation={stopEditorShortcutPropagation}
      />
      {tableToolbarState.visible && !isHtmlMode && (
        <div
          ref={floatingTableToolbarRef}
          className="eb-QuillText__table-toolbar"
          style={{
            top: `${tableToolbarState.top}px`,
            left: `${tableToolbarState.left}px`,
          }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            className="eb-QuillText__table-toolbar-btn"
            onClick={() => {
              const quill = quillRef.current;
              const tableModule = quill?.getModule("table") as any;
              tableModule?.insertRowAbove();
              if (quill) {
                setTimeout(() => fixEditorTableStructure(quill.root), 0);
              }
            }}
          >
            + Row Above
          </button>
          <button
            type="button"
            className="eb-QuillText__table-toolbar-btn"
            onClick={() => {
              const quill = quillRef.current;
              const tableModule = quill?.getModule("table") as any;
              tableModule?.insertRowBelow();
              if (quill) {
                setTimeout(() => fixEditorTableStructure(quill.root), 0);
              }
            }}
          >
            + Row Below
          </button>
          <button
            type="button"
            className="eb-QuillText__table-toolbar-btn"
            onClick={() => {
              const quill = quillRef.current;
              const tableModule = quill?.getModule("table") as any;
              tableModule?.insertColumnLeft();
              if (quill) {
                setTimeout(() => fixEditorTableStructure(quill.root), 0);
              }
            }}
          >
            + Col Left
          </button>
          <button
            type="button"
            className="eb-QuillText__table-toolbar-btn"
            onClick={() => {
              const quill = quillRef.current;
              const tableModule = quill?.getModule("table") as any;
              tableModule?.insertColumnRight();
              if (quill) {
                setTimeout(() => fixEditorTableStructure(quill.root), 0);
              }
            }}
          >
            + Col Right
          </button>
          <span className="eb-QuillText__table-toolbar-sep" />
          <button
            type="button"
            className="eb-QuillText__table-toolbar-btn eb-QuillText__table-toolbar-btn--danger"
            onClick={() => {
              const quill = quillRef.current;
              const tableModule = quill?.getModule("table") as any;
              tableModule?.deleteRow();
            }}
          >
            − Row
          </button>
          <button
            type="button"
            className="eb-QuillText__table-toolbar-btn eb-QuillText__table-toolbar-btn--danger"
            onClick={() => {
              const quill = quillRef.current;
              const tableModule = quill?.getModule("table") as any;
              tableModule?.deleteColumn();
            }}
          >
            − Col
          </button>
          <button
            type="button"
            className="eb-QuillText__table-toolbar-btn eb-QuillText__table-toolbar-btn--danger"
            onClick={() => {
              const quill = quillRef.current;
              const tableModule = quill?.getModule("table") as any;
              tableModule?.deleteTable();
              setTableToolbarState({ visible: false, top: 0, left: 0, styles: [], hasHeader: false, cellHAlign: "", cellVAlign: "" });
            }}
          >
            Delete Table
          </button>
          <div className="eb-QuillText__table-toolbar-row2">
            <span className="eb-QuillText__table-toolbar-label">Border:</span>
            {BOOTSTRAP_TABLE_BORDER_STYLES.map((s) => {
              // Determine which border style is active
              // Find if any border style (bordered/borderless) is in the styles array
              const activeBorderStyle = tableToolbarState.styles.find(style =>
                BOOTSTRAP_TABLE_BORDER_STYLES.some(bs => bs.key === style && bs.key !== "default")
              );
              // If no border style found, "default" is active
              const isActive = activeBorderStyle
                ? s.key === activeBorderStyle
                : s.key === "default";

              return (
                <button
                  key={s.key}
                  type="button"
                  className={[
                    "eb-QuillText__table-toolbar-btn",
                    isActive ? "is-active" : "",
                  ].filter(Boolean).join(" ")}
                  onClick={() => toggleTableStyle(s.key)}
                >
                  {s.label}
                </button>
              );
            })}
            <span className="eb-QuillText__table-toolbar-sep" />
            <span className="eb-QuillText__table-toolbar-label">Style:</span>
            {BOOTSTRAP_TABLE_OTHER_STYLES.map((s) => (
              <button
                key={s.key}
                type="button"
                className={[
                  "eb-QuillText__table-toolbar-btn",
                  tableToolbarState.styles.includes(s.key) ? "is-active" : "",
                ].filter(Boolean).join(" ")}
                onClick={() => toggleTableStyle(s.key)}
              >
                {s.label}
              </button>
            ))}
            <span className="eb-QuillText__table-toolbar-sep" />
            <button
              type="button"
              className={[
                "eb-QuillText__table-toolbar-btn",
                tableToolbarState.hasHeader ? "is-active" : "",
              ].filter(Boolean).join(" ")}
              onClick={() => toggleTableHeader()}
            >
              Header Row
            </button>
          </div>
          <div className="eb-QuillText__table-toolbar-row2">
            <span className="eb-QuillText__table-toolbar-label">Cell H-Align:</span>
            <button
              type="button"
              className={[
                "eb-QuillText__table-toolbar-btn",
                tableToolbarState.cellHAlign === "" ? "is-active" : "",
              ].filter(Boolean).join(" ")}
              onClick={() => setCellHAlign("")}
            >
              Default
            </button>
            <button
              type="button"
              className={[
                "eb-QuillText__table-toolbar-btn",
                tableToolbarState.cellHAlign === "start" ? "is-active" : "",
              ].filter(Boolean).join(" ")}
              onClick={() => setCellHAlign("start")}
            >
              Left
            </button>
            <button
              type="button"
              className={[
                "eb-QuillText__table-toolbar-btn",
                tableToolbarState.cellHAlign === "center" ? "is-active" : "",
              ].filter(Boolean).join(" ")}
              onClick={() => setCellHAlign("center")}
            >
              Center
            </button>
            <button
              type="button"
              className={[
                "eb-QuillText__table-toolbar-btn",
                tableToolbarState.cellHAlign === "end" ? "is-active" : "",
              ].filter(Boolean).join(" ")}
              onClick={() => setCellHAlign("end")}
            >
              Right
            </button>
            <span className="eb-QuillText__table-toolbar-sep" />
            <span className="eb-QuillText__table-toolbar-label">Cell V-Align:</span>
            <button
              type="button"
              className={[
                "eb-QuillText__table-toolbar-btn",
                tableToolbarState.cellVAlign === "" ? "is-active" : "",
              ].filter(Boolean).join(" ")}
              onClick={() => setCellVAlign("")}
            >
              Default
            </button>
            <button
              type="button"
              className={[
                "eb-QuillText__table-toolbar-btn",
                tableToolbarState.cellVAlign === "top" ? "is-active" : "",
              ].filter(Boolean).join(" ")}
              onClick={() => setCellVAlign("top")}
            >
              Top
            </button>
            <button
              type="button"
              className={[
                "eb-QuillText__table-toolbar-btn",
                tableToolbarState.cellVAlign === "middle" ? "is-active" : "",
              ].filter(Boolean).join(" ")}
              onClick={() => setCellVAlign("middle")}
            >
              Middle
            </button>
            <button
              type="button"
              className={[
                "eb-QuillText__table-toolbar-btn",
                tableToolbarState.cellVAlign === "bottom" ? "is-active" : "",
              ].filter(Boolean).join(" ")}
              onClick={() => setCellVAlign("bottom")}
            >
              Bottom
            </button>
          </div>
        </div>
      )}
      <MediaBrowserModal
        isOpen={isImageModalOpen}
        instanceId={mediaBrowserInstanceIdRef.current}
        onClose={handleCloseImageModal}
        onPick={handlePickImage}
        uploadContext="wysiwyg"
      />
    </div>
  );
}

export default QuillTextEditor;
